import { useEffect, useState } from 'react';
import { Link, Route, Routes } from 'react-router';
import type { MeResponse } from '@mmf/contracts';
import { AccountPage } from './account/AccountPage';
import { CategoryAdmin } from './admin/CategoryAdmin';
import { MemberAdmin } from './admin/MemberAdmin';
import { ModerationQueue } from './admin/ModerationQueue';
import { ReportsPage } from './admin/ReportsPage';
import { TriggerWordRuleAdmin } from './admin/TriggerWordRuleAdmin';
import { externalSignInUrl, signInUrl } from './api/client';
import { hasAnyRole, hasRole, useMe } from './auth/useMe';
import mortgagemaxLogo from './assets/mortgagemax-logo.png';
import { MainCategoryList } from './forum/MainCategoryList';
import { TopicDetail } from './forum/TopicDetail';
import { ScopedTopicList, TopicList } from './forum/TopicList';
import { CommunityGuidelines } from './landing/CommunityGuidelines';
import { LandingPage } from './landing/LandingPage';
import { LegalPlaceholder } from './landing/LegalPlaceholder';

/**
 * The walking skeleton: prove that an employee can sign in through Entra and
 * that the API can tell us who they are, what they may do, and whether they may
 * currently post. Everything else in the forum builds on exactly this.
 */
export function App(): React.JSX.Element {
  const { principal, isLoading, isSignedOut, isRefused, error } = useMe();

  if (isLoading) {
    return (
      <Shell>
        <p role="status" data-testid="loading">
          Checking your access…
        </p>
      </Shell>
    );
  }

  if (isSignedOut) {
    return (
      <Shell>
        <h2>Sign in</h2>
        <SignInFailureNotice />
        <SignInChooser />
      </Shell>
    );
  }

  if (isRefused) {
    return (
      <Shell>
        <h2>No access</h2>
        <p data-testid="refused">
          You are signed in, but you are not a member of this forum. Ask an administrator to add
          you.
        </p>
      </Shell>
    );
  }

  if (error || !principal) {
    return (
      <Shell>
        <h2>Something went wrong</h2>
        <p role="alert" data-testid="error">
          We could not load your profile. Try again shortly, and let the service desk know if it
          keeps happening.
        </p>
      </Shell>
    );
  }

  const isAdministrator = hasRole(principal, 'Administrator');
  const isModerator = hasAnyRole(principal, ['Moderator', 'Administrator']);

  return (
    <Shell
      nav={
        <nav className="main-nav" aria-label="Forum">
          <Link to="/">Home</Link>
          <Link to="/discussions">Discussions</Link>
          {/* Courtesy only — hiding the link is not the security boundary, the
              API guard behind each /admin/* route is. Omitted below entirely
              for anyone without the role. */}
          {isModerator && <Link to="/admin/moderation">Moderation Queue</Link>}
          {isAdministrator && <Link to="/admin/categories">Manage Categories</Link>}
          {isModerator && <Link to="/admin/members">Manage Members</Link>}
          {isAdministrator && <Link to="/admin/trigger-rules">Trigger Words</Link>}
          {isAdministrator && <Link to="/admin/reports">Reports</Link>}
        </nav>
      }
      actions={<UserMenu principal={principal} />}
    >
      <SignedInContent principal={principal} isAdministrator={isAdministrator} isModerator={isModerator} />
    </Shell>
  );
}

function UserMenu({ principal }: { principal: MeResponse }): React.JSX.Element {
  return (
    <div className="user-menu">
      {/* The name itself is the way into /account — no separate nav link. */}
      <Link to="/account" className="user-menu-name">
        {principal.displayName}
      </Link>
      {/* Sign-out is a POST so a link preview or prefetch cannot trigger it. */}
      <form method="post" action="/api/auth/logout">
        <button type="submit" className="button secondary small" data-testid="sign-out">
          Sign out
        </button>
      </form>
    </div>
  );
}

function SignedInContent({
  principal,
  isAdministrator,
  isModerator,
}: {
  principal: MeResponse;
  isAdministrator: boolean;
  isModerator: boolean;
}): React.JSX.Element {
  return (
    <>
      {/* Actionable wherever the member is, not something they'd have to
          visit /account to discover — everything else about "who am I" is
          on the Account page instead. */}
      {!principal.canParticipate && (
        <p className="notice warning" role="alert" data-testid="suspended-notice">
          Your posting access is suspended. You can still read the forum.
        </p>
      )}

      <Routes>
        <Route path="/" element={<LandingPage displayName={principal.displayName} />} />
        <Route path="/discussions" element={<MainCategoryList />} />
        <Route path="/discussions/all" element={<TopicList />} />
        <Route path="/discussions/:mainCategoryId" element={<ScopedTopicList />} />
        <Route path="/topics/:id" element={<TopicDetail />} />
        <Route path="/account" element={<AccountPage principal={principal} />} />
        <Route path="/guidelines" element={<CommunityGuidelines standalone />} />
        <Route path="/privacy" element={<LegalPlaceholder title="Privacy Notice" />} />
        <Route path="/terms" element={<LegalPlaceholder title="Terms of Use" />} />
        {isModerator && <Route path="/admin/moderation" element={<ModerationQueue />} />}
        {isAdministrator && <Route path="/admin/categories" element={<CategoryAdmin />} />}
        {isModerator && (
          <Route
            path="/admin/members"
            element={<MemberAdmin isAdministrator={isAdministrator} currentUserId={principal.userId} />}
          />
        )}
        {isAdministrator && <Route path="/admin/trigger-rules" element={<TriggerWordRuleAdmin />} />}
        {isAdministrator && <Route path="/admin/reports" element={<ReportsPage />} />}
      </Routes>
    </>
  );
}

/**
 * `/api/auth/callback` redirects failures to `/signed-out?reason=...` rather
 * than showing anything itself — it deliberately never leaks Entra's own
 * error detail to the browser (see AuthController). Without this, that lands
 * back on this exact "Sign in" screen with the failure query string just
 * sitting there unread, which looks indistinguishable from having clicked
 * nothing at all.
 */
function SignInFailureNotice(): React.JSX.Element | null {
  const [reason] = useState(() => new URLSearchParams(window.location.search).get('reason'));

  // Strip it from the address bar once read, so refreshing (or bookmarking)
  // this screen does not keep re-showing a stale failure. Replaced with "/",
  // not just the query string trimmed off — /signed-out itself is never a
  // legitimate place to return to. SignInChooser's links below default their
  // returnTo to window.location.pathname at click time, so leaving the
  // address bar on /signed-out would make a *successful* retry redirect
  // right back to this failure screen instead of into the app.
  useEffect(() => {
    if (reason) {
      window.history.replaceState(null, '', '/');
    }
  }, [reason]);

  if (reason === 'denied') {
    return (
      <p className="notice warning" role="alert" data-testid="sign-in-denied">
        You signed in, but you are not a member of this forum yet. Ask an administrator to add
        you.
      </p>
    );
  }

  if (reason === 'failed') {
    return (
      <p className="notice warning" role="alert" data-testid="sign-in-failed">
        Sign-in did not complete. This is usually a configuration problem rather than something
        you did wrong — try again, and let the service desk know if it keeps happening.
      </p>
    );
  }

  return null;
}

/**
 * One button, not two competing ones. Staff and business partners are two
 * genuinely separate Entra tenants under the hood (see ExternalAuthController)
 * — but a visitor should not have to know that just to find the sign-in
 * button. Clicking "Sign in" reveals the two real destinations rather than
 * picking one for them.
 */
function SignInChooser(): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false);

  if (!isOpen) {
    return (
      <>
        <p>Use your MortgageMax account to continue.</p>
        <button
          type="button"
          className="button"
          onClick={() => setIsOpen(true)}
          data-testid="sign-in-start"
        >
          Sign in
        </button>
      </>
    );
  }

  return (
    <>
      <p>Continue as:</p>
      <div className="sign-in-options">
        <a className="button" href={signInUrl()} data-testid="sign-in">
          MortgageMAX staff
        </a>
        <a className="button secondary" href={externalSignInUrl()} data-testid="sign-in-external">
          Business partner
        </a>
      </div>
    </>
  );
}

/**
 * The sticky top bar (logo, nav, user menu) is always the same shape whether
 * signed in or not — `nav`/`actions` are simply absent on the sign-in/
 * loading/error screens.
 */
function Shell({
  nav,
  actions,
  children,
}: {
  nav?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="topbar-inner">
          {/* h1 stays in the DOM for a11y/SEO — the logo already carries the
              name visually, so the heading text is screen-reader-only. */}
          <h1>
            <Link to="/" className="topbar-brand">
              {/* Decorative — the visually-hidden text is this link's one
                  accessible name. A repeated `alt` here would have a screen
                  reader announce "MortgageMAX Forum" twice. */}
              <img className="brand-logo" src={mortgagemaxLogo} alt="" />
              <span className="visually-hidden">MortgageMAX Forum</span>
            </Link>
          </h1>
          {nav}
          <div className="topbar-actions">{actions}</div>
        </div>
      </header>

      <main className="page">
        <div className="page-inner">{children}</div>
      </main>

      <footer className="site-footer">
        <div className="site-footer-inner">
          <nav className="footer-links" aria-label="Legal and support">
            <Link to="/guidelines">Community Guidelines</Link>
            <Link to="/privacy">Privacy Notice</Link>
            <Link to="/terms">Terms of Use</Link>
            <a href="mailto:csc@betterhome.co.za">Support</a>
          </nav>
          <p>
            This forum is for discussion and collaboration only — it is not an official support
            channel. For account issues or urgent requests, contact the Client Support Centre at{' '}
            <a href="mailto:csc@betterhome.co.za">csc@betterhome.co.za</a>.
          </p>
          <p>Internal use only. Do not post client personal information or screenshots of client records.</p>
        </div>
      </footer>
    </div>
  );
}
